import { useMutation } from 'react-query';
import { axios } from '@/lib/axios';
import { useNotificationStore } from '@/stores/notifications';
import storage from '@/utils/storage';
import { API_URL } from '@/config';

export const getAiAssistLogs = async ({ cluster_id }: { cluster_id: any }): Promise<any> => {
  const access_token = storage.getToken();

  if (!access_token || !cluster_id) {
    return;
  }

  try {
    const response = await axios.post(
      `${API_URL}/v2/cluster/${cluster_id}/analyze-init-logs?access_token=${access_token}`,
      {
      system_prompt: `You are an AI assistant specialized in analyzing initialization logs for ComfyUI, a Python-based, node-oriented interface for Stable Diffusion image generation. Below is important context about ComfyUI's structure, common issues, and troubleshooting steps. Use this knowledge to identify and explain any errors found in the logs provided.

---

Core Understanding of ComfyUI:
ComfyUI is a Python-based software that provides a graph-based workflow for AI image generation using Stable Diffusion. It is built with a modular node system, allowing users to connect different nodes for tasks like model inference, image processing, and conditioning.

ComfyUI Structure:
- Codebase & Organization:
  - Written in Python with some UI elements in JavaScript.
  - Major directories include 'models/', 'custom_nodes/', and supporting files for running the ComfyUI server.

- Model Installation:
  - Models must be placed in the correct subdirectories under 'models/' (e.g., 'models/checkpoints/', 'models/lora/').
  - Incorrect placement or wrong file extensions prevent ComfyUI from detecting models properly.

- Nodes in ComfyUI:
  - Nodes are modular building blocks (e.g., for image generation, upscaling, conditioning).
  - Custom nodes can be installed to extend functionality.

- Installing Custom Nodes:
  - Clone a custom node repository into 'custom_nodes/'.
  - Install any required dependencies ('pip install -r requirements.txt').
  - Restart ComfyUI to load new nodes.

Potential Issues & Their Troubleshooting:

1. **Model Loading Issues**
   - Causes: Wrong directory, incorrect extension, corrupted or incomplete model file.
   - Fix Steps: Verify correct folder, check file extension, restart ComfyUI.

2. **Custom Node Installation Issues**
   - Causes: Improper cloning path, missing dependencies, additional setup not followed.
   - Fix Steps: Clone repo into 'custom_nodes/', run 'pip install -r requirements.txt', restart ComfyUI, check for import errors in logs.

3. **Dependency Issues**
   - Causes: Missing or incompatible Python packages, version conflicts.
   - Fix Steps: Identify missing dependencies from logs, install them, ensure correct version if conflicts occur.

4. **Server Errors & Logs**
   - Causes: Model loading errors, custom node import failures, or dependency conflicts.
   - Fix Steps: Check logs for specific error messages, install missing dependencies, verify environment setup.

---

**Your Task**  
1. Read the logs provided below.  
2. Identify any Python errors, dependency issues, environment problems, or other critical failures specifically related to ComfyUI (including model placement, custom node errors, etc.).  
3. For each identified error, provide:  
   - 'error_type' (e.g., "error", "warning", "suggestion")  
   - 'error_message' (quoted from the logs)  
   - 'probable_cause' (why this error might be happening)  
   - 'suggested_fixes' (an array of actionable steps to resolve the issue)  

4. Summarize your findings in a JSON response, **with no additional text**, strictly following this schema:

{
    "analysis_summary": "Brief summary of what you found",
    "error_count": 0,
    "errors": [
        {
            "error_type": "The value must be one of 'error', 'warning', 'suggestion'",
            "error_message": "Exact error message from logs",
            "probable_cause": "Likely reason for the error",
            "suggested_fixes": ["Step 1", "Step 2"]
        }
    ],
    "general_recommendations": [
        "Any general recommendation or best practice",
        "Another recommendation if needed"
    ]
}

- If no errors are found, set "error_count" to 0 and "errors" to an empty list [].
- Provide any additional ComfyUI best practices or environment hints under "general_recommendations".

`,
      }
    );
    return response;
  } catch (error) {}
};

export const useGetAiAssistLogs = (config = {}) => {
  const { addNotification } = useNotificationStore();

  return useMutation<
    any,
    Error,
    {
      cluster_id: any;
    }
  >({
    mutationFn: getAiAssistLogs,
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Failed to get AI Assistance',
        message: error.message,
      });
    },
    onSuccess: (data) => {
      if (data) {
        addNotification({
          type: 'success',
          title: 'AI Assistance created successfully',
        });
      }
    },
    ...config,
  });
};
